<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useWahaCall } from 'dashboard/composables/useWahaCall';
import ButtonV4 from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();

const {
  contactName,
  contactPhone,
  elapsed,
  errorMsg,
  isMuted,
  isResolving,
  isCalling,
  isInCall,
  isIncoming,
  hasError,
  acceptIncoming,
  rejectIncoming,
  toggleMute,
  hangup,
  resetCall,
} = useWahaCall();

const elapsedFormatted = computed(() => {
  const s = elapsed.value;
  const mm = String(Math.floor(s / 60)).padStart(2, '0');
  const ss = String(s % 60).padStart(2, '0');
  return `${mm}:${ss}`;
});

const statusLabel = computed(() => {
  if (isResolving.value) return t('WACALLS.STATUS.RESOLVING');
  if (isCalling.value) return t('WACALLS.STATUS.CALLING');
  if (isInCall.value) return elapsedFormatted.value;
  if (isIncoming.value) return t('WACALLS.STATUS.INCOMING');
  if (hasError.value) return errorMsg.value;
  return '';
});
</script>

<template>
  <Teleport to="body">
    <div
      class="fixed bottom-5 right-5 z-[99999] w-72 rounded-2xl overflow-hidden shadow-2xl border border-n-weak bg-n-solid-1"
    >
      <div
        class="flex items-center gap-2 px-4 py-3 border-b border-n-weak bg-n-solid-2"
      >
        <span
          class="size-2 rounded-full bg-green-500 flex-none animate-pulse"
        />
        <span class="flex-1 text-sm font-semibold text-n-slate-12">
          {{ $t('WACALLS.PANEL_TITLE') }}
        </span>
        <button
          class="text-n-slate-9 hover:text-n-slate-12 transition-colors leading-none text-xl font-light"
          @click="resetCall"
        >
          {{ $t('WACALLS.CLOSE') }}
        </button>
      </div>

      <div class="px-4 py-5 text-center space-y-1">
        <div
          class="mx-auto mb-3 size-14 rounded-full bg-n-alpha-3 flex items-center justify-center"
        >
          <svg
            viewBox="0 0 24 24"
            class="size-7 text-green-500"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path
              d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.8 1.24h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 8.91a16 16 0 0 0 6 6l.96-.96a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z"
            />
          </svg>
        </div>
        <div class="text-base font-semibold text-n-slate-12 truncate">
          {{ contactName || t('WACALLS.UNKNOWN_CONTACT') }}
        </div>
        <div class="text-sm text-n-slate-10 truncate">{{ contactPhone }}</div>
        <div
          class="text-xs font-medium min-h-4"
          :class="hasError ? 'text-red-500' : 'text-n-slate-9'"
        >
          {{ statusLabel }}
        </div>
      </div>

      <div class="flex items-center justify-center gap-4 pb-6">
        <template v-if="isIncoming">
          <button
            class="size-14 rounded-full bg-green-500 hover:bg-green-600 active:scale-95 transition-all flex items-center justify-center text-white shadow-lg"
            :title="t('WACALLS.ACCEPT')"
            @click="acceptIncoming"
          >
            <svg
              viewBox="0 0 24 24"
              class="size-6"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path
                d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.8 1.24h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 8.91a16 16 0 0 0 6 6l.96-.96a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z"
              />
            </svg>
          </button>
          <button
            class="size-14 rounded-full bg-red-500 hover:bg-red-600 active:scale-95 transition-all flex items-center justify-center text-white shadow-lg"
            :title="t('WACALLS.REJECT')"
            @click="rejectIncoming"
          >
            <svg
              viewBox="0 0 24 24"
              class="size-6"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </template>

        <template v-else-if="isCalling || isInCall">
          <button
            class="size-12 rounded-full flex items-center justify-center transition-all"
            :class="
              isMuted
                ? 'bg-red-500 text-white'
                : 'bg-n-alpha-3 text-n-slate-9 hover:bg-n-alpha-4'
            "
            :title="isMuted ? t('WACALLS.UNMUTE') : t('WACALLS.MUTE')"
            @click="toggleMute"
          >
            <svg
              v-if="!isMuted"
              viewBox="0 0 24 24"
              class="size-5"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
              <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
              <line x1="12" y1="19" x2="12" y2="23" />
            </svg>
            <svg
              v-else
              viewBox="0 0 24 24"
              class="size-5"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <line x1="1" y1="1" x2="23" y2="23" />
              <path
                d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6"
              />
              <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23" />
              <line x1="12" y1="19" x2="12" y2="23" />
            </svg>
          </button>
          <button
            class="size-14 rounded-full bg-red-500 hover:bg-red-600 active:scale-95 transition-all flex items-center justify-center text-white shadow-lg"
            :title="t('WACALLS.HANGUP')"
            @click="hangup"
          >
            <svg
              viewBox="0 0 24 24"
              class="size-6"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path
                d="M10.68 13.31a16 16 0 0 0 3.41 2.6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7 2 2 0 0 1 1.72 2v3a2 2 0 0 1-2.18 2A19.79 19.79 0 0 1 3.07 8.94a2 2 0 0 1 2-2.18h3"
              />
              <line x1="23" y1="1" x2="1" y2="23" />
            </svg>
          </button>
        </template>

        <template v-else-if="hasError">
          <ButtonV4
            size="sm"
            variant="ghost"
            color="slate"
            :label="t('WACALLS.CLOSE')"
            @click="resetCall"
          />
        </template>

        <template v-else-if="isResolving">
          <div
            class="size-8 border-2 border-n-slate-5 border-t-green-500 rounded-full animate-spin"
          />
        </template>
      </div>
    </div>
  </Teleport>
</template>
